import { useMemo } from "react";
import type { BulkAction } from "@salestrak/ui";

import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import type { IssueRow, StageOption } from "#app/lib/data";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { ASSIGNEE_ICON } from "#app/lib/entity-visuals";
import { useWorkflowPool } from "#app/components/entity-selectors/hooks";
import {
  BulkAssigneeContent,
  BulkStageContent,
  buildAssignToMeAction,
  buildBulkArchiveAction,
  buildBulkCustomFieldAction,
  buildBulkDeleteAction,
  buildBulkMoveWorkflowAction,
  buildBulkSourceAction,
  buildBulkTeamsAction,
  buildBulkUnarchiveAction,
  buildSelectAllWhere,
} from "#app/components/issues/issue-bulk-actions";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { useAnalytics, type AnalyticsScope, type AnalyticsView } from "#app/lib/analytics";

/**
 * THE project bulk roster — one builder for every surface that selects
 * project rows: the list and board, saved views, Team and Member pages,
 * Shared, a dashboard widget's table and its Data drilldown, the Account and
 * Contact linked-projects tab. The strip seats the properties people batch-
 * edit daily, glyphs for the lifecycle verbs, and folds the rest into More:
 *
 *   Stage · Assignees · Teams · [host extras] · 📦 Archive/Unarchive · 🗑 | ⋯ More
 *
 * More lists everything above plus the dialog-only actions — Assign to Me,
 * Change Source, Set Custom Field, Move to Workflow — in the entity menu's
 * order (properties · item · lifecycle). Follow up and Project Type are NOT
 * bulk actions on purpose: a follow up is a promise about one project, and a
 * type change runs a per-project migration for its required fields.
 *
 * Stage and Assignees have where-based mutations, so they reach a select-all
 * through `getGroupWhere`; every other action writes per row and steps aside
 * under select-all (unloaded rows are out of reach by id).
 */
export function useIssueBulkActions({
  view,
  scope,
  workflowId,
  availableStages,
  refetchGroups,
  getGroupWhere,
  extraBulkActions,
}: {
  /** Which view the bulk operation happens on, for analytics. */
  view: AnalyticsView;
  /** The scope kind that view is showing, for analytics. */
  scope: AnalyticsScope;
  /** The ONE workflow the selection can change stage within; a cross-workflow
   *  scope has no single stage target and gets no Stage action. */
  workflowId: string | undefined;
  availableStages: StageOption[];
  refetchGroups: () => Promise<unknown>;
  /** Resolves a select-all group to its where clause (grouped lists only);
   *  without it, popover commits run on the loaded rows. */
  getGroupWhere?: (groupKey: string) => Record<string, unknown>;
  /** Host-specific actions ("Remove from Account"), seated after Teams. */
  extraBulkActions?: BulkAction<IssueRow>[] | undefined;
}): BulkAction<IssueRow>[] {
  const { track } = useAnalytics();
  const { currentUser } = useAppData();
  const { bulk: bulkMutations } = useIssueListMutations();
  const { customFields } = useWorkspaceData();
  // Move to Workflow needs somewhere to move to: a second editable workflow.
  const { pool: workflowPool } = useWorkflowPool({ editableOnly: true });
  const hasMoveTargets = workflowPool.items.length > 1;
  const hasCustomFields = customFields.length > 0;

  return useMemo<BulkAction<IssueRow>[]>(() => {
    const actions: BulkAction<IssueRow>[] = [];
    const whereOf = (groups: Parameters<typeof buildSelectAllWhere>[0]) =>
      getGroupWhere ? buildSelectAllWhere(groups, getGroupWhere) : undefined;
    // The selection's full size: under select-all, the server's group totals
    // (unloaded rows included); otherwise the loaded rows.
    const countOf = (
      selectedRows: IssueRow[],
      groups: Parameters<typeof buildSelectAllWhere>[0],
    ) =>
      groups.length > 0
        ? groups.reduce((sum, g) => sum + g.totalCount, 0)
        : selectedRows.length;

    if (workflowId && availableStages.length > 0) {
      actions.push({
        id: "change-stage",
        type: "popover",
        label: "Stage",
        dialogLabel: "Change Stage...",
        icon: PROPERTY_ICON.stage,
        contentProps: { padding: "none" },
        content: ({ selectedRows, selectAllGroups, commit }) => {
          const selectAllWhere = whereOf(selectAllGroups);
          return (
            <BulkStageContent
              view={view}
              scope={scope}
              workflowId={selectedRows[0]?.workflowId ?? workflowId}
              commit={commit}
              bulkMutations={bulkMutations}
              refetchGroups={refetchGroups}
              availableStages={availableStages}
              selectedCount={countOf(selectedRows, selectAllGroups)}
              selectedRows={selectedRows}
              selectAllWhere={selectAllWhere}
            />
          );
        },
      });
    }

    actions.push({
      id: "set-assignees",
      type: "popover",
      label: "Assignees",
      dialogLabel: "Set Assignees...",
      icon: ASSIGNEE_ICON,
      // No pixel width here: the body floors itself at the builder rung
      // (the list mode's avatar · name · "Set primary" row needs more than
      // the picker rung), so the popup is on the one width table.
      contentProps: { padding: "none" },
      content: ({ selectedRows, selectAllGroups }) => (
        <BulkAssigneeContent
          view={view}
          scope={scope}
          bulkMutations={bulkMutations}
          selectedRows={selectedRows}
          selectedCount={countOf(selectedRows, selectAllGroups)}
          selectAllWhere={whereOf(selectAllGroups)}
        />
      ),
    });

    if (currentUser) {
      actions.push(
        buildAssignToMeAction({
          view,
          scope,
          bulkMutations,
          currentUserId: currentUser.userId,
          track,
        }),
      );
    }

    actions.push(buildBulkTeamsAction({ view, scope, bulkMutations }));

    if (extraBulkActions) actions.push(...extraBulkActions);

    actions.push(buildBulkSourceAction({ view, scope, bulkMutations }));
    if (hasCustomFields) {
      actions.push(buildBulkCustomFieldAction({ view, scope, bulkMutations }));
    }
    if (hasMoveTargets) {
      actions.push(
        buildBulkMoveWorkflowAction({ view, scope, bulkMutations }),
      );
    }

    actions.push(buildBulkArchiveAction({ view, scope, bulkMutations, track }));
    actions.push(buildBulkUnarchiveAction({ view, scope, bulkMutations, track }));
    actions.push(buildBulkDeleteAction({ view, scope, bulkMutations, track }));

    return actions;
  }, [
    view,
    scope,
    workflowId,
    availableStages,
    refetchGroups,
    getGroupWhere,
    extraBulkActions,
    currentUser,
    bulkMutations,
    hasCustomFields,
    hasMoveTargets,
    track,
  ]);
}
